import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, type Order, type OrderFromInquiryPreview } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './disposition.css';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const dayDe = (iso: string) => iso.split('-').reverse().join('.');
/** „89,50“ → 8950, ungültig → null */
const parseEuro = (text: string): number | null => {
  const n = Number(text.trim().replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
};

/**
 * Auftrag direkt aus der Anfrage (KON-238). Im Chauffeur-Betrieb steht der Preis oft schon in der Anfrage, dann braucht es kein Angebot: Das Fenster zeigt, was Wirkia übernimmt (Fahrt, Abholung,
 * Preis, Kunde), und legt den Auftrag an. Alle Angaben und der Preis der Anfrage gelten für Auftrag und spätere Rechnung. Fehlt der Preis, trägt der Dispatcher ihn hier ein. Zu einer Anfrage
 * gibt es nur einen laufenden Auftrag: Gibt es schon einen, führt das Fenster dorthin. Der Weg über ein individuelles Angebot bleibt daneben bestehen.
 */
export function InquiryOrderButton({ inquiryId }: { inquiryId: string }) {
  const { can, hasModule, session } = useSession();
  const [open, setOpen] = useState(false);
  // Der Auftrag direkt aus der Anfrage gehört zum Chauffeur-Betrieb: Der Preis steht in der Anfrage, ein Angebot ist oft überflüssig
  if (session?.tenant.industry !== 'chauffeur' || !hasModule('orders') || !can('orders.write') || !can('inquiries.read')) return null;
  return (
    <>
      <button className="btn btn--primary btn--sm" type="button" onClick={() => setOpen(true)}>Auftrag erstellen</button>
      {open && <InquiryOrderDialog inquiryId={inquiryId} onClose={() => setOpen(false)} />}
    </>
  );
}

function InquiryOrderDialog({ inquiryId, onClose }: { inquiryId: string; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const client = useQueryClient();
  const [priceText, setPriceText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const preview = useQuery({ queryKey: ['order-from-inquiry', inquiryId], queryFn: async () => (await api<{ data: OrderFromInquiryPreview }>('GET', `/orders/from-inquiry/${inquiryId}`)).data });

  useEffect(() => {
    const el = dialog.current;
    if (el && !el.open) el.showModal();
  }, []);

  const p = preview.data;
  const entered = parseEuro(priceText);
  const price = p?.priceGrossCents ?? entered;
  const create = useMutation({
    mutationFn: async () => (await api<{ data: Order }>('POST', `/orders/from-inquiry/${inquiryId}`, p?.priceGrossCents ? {} : { priceGrossCents: entered })).data,
    onSuccess: (order) => {
      client.invalidateQueries({ queryKey: ['inquiry', inquiryId] });
      client.invalidateQueries({ queryKey: ['inquiries'] });
      client.invalidateQueries({ queryKey: ['orders'] });
      navigate(`/auftraege/${order.id}`);
    },
    onError: (e) => setError(errorText(e, 'Der Auftrag konnte nicht angelegt werden.')),
  });

  return (
    <dialog
      ref={dialog}
      className="dialog dialog--wide"
      aria-labelledby="inquiry-order-title"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialog.current && !create.isPending) onClose();
      }}
    >
      <div className="dialog__body dispo-dialog">
        <h2 id="inquiry-order-title">Auftrag aus Anfrage erstellen</h2>
        {preview.isLoading && <p className="muted">Wird geladen …</p>}
        {preview.isError && <div className="alert alert--error" role="alert">Die Anfrage konnte nicht geladen werden.</div>}
        {p && p.existingOrder && (
          <>
            <div className="alert alert--warn" role="status">Zu dieser Anfrage gibt es schon den Auftrag {p.existingOrder.number}. Ein zweiter Auftrag wäre eine Doppelung.</div>
            <div className="form-actions">
              <Link className="btn btn--primary" to={`/auftraege/${p.existingOrder.id}`}>Zum Auftrag {p.existingOrder.number}</Link>
              <button className="btn btn--secondary" type="button" onClick={onClose}>Schließen</button>
            </div>
          </>
        )}
        {p && !p.existingOrder && (
          <>
            <p className="muted small">Wirkia übernimmt die Angaben der Anfrage {p.reference} in einen Auftrag. Der Preis aus der Anfrage gilt für den Auftrag und später für die Rechnung. Möchten Sie dem Kunden einen eigenen Preis anbieten, erstellen Sie stattdessen ein Angebot.</p>
            <dl className="dispo-facts">
              <dt>Fahrt</dt>
              <dd>{p.subject}</dd>
              <dt>Abholung</dt>
              <dd>{p.pickupDate ? `${dayDe(p.pickupDate)}${p.pickupTime ? `, ${p.pickupTime} Uhr` : ', Uhrzeit fehlt'}` : <span className="muted">Datum fehlt, Sie tragen es im Auftrag nach</span>}</dd>
              <dt>Kunde</dt>
              <dd>{p.contactName}<span className="muted small">{p.contactExists ? ' (ist schon im Kontaktbuch)' : ' (wird im Kontaktbuch angelegt)'}</span></dd>
              <dt>Preis</dt>
              <dd>
                {p.priceGrossCents ? (
                  <><strong>{formatCents(p.priceGrossCents)}</strong> <span className="muted small">brutto, aus der Anfrage</span></>
                ) : (
                  <label className="field dispo-price">
                    <span className="muted small">Die Anfrage enthält keinen Preis. Bitte tragen Sie den vereinbarten Bruttopreis ein.</span>
                    <input className="input" inputMode="decimal" value={priceText} onChange={(e) => setPriceText(e.target.value)} placeholder="zum Beispiel 89,50" aria-label="Bruttopreis in Euro" />
                  </label>
                )}
              </dd>
            </dl>
            {error && <div className="alert alert--error" role="alert">{error}</div>}
            <div className="form-actions">
              <button className="btn btn--primary" type="button" disabled={create.isPending || !price} onClick={() => { setError(null); create.mutate(); }}>
                {create.isPending ? 'Wird angelegt …' : 'Auftrag anlegen'}
              </button>
              <button className="btn btn--secondary" type="button" disabled={create.isPending} onClick={onClose}>Abbrechen</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  );
}
